import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import {
    addSuppliersSelection,
    clearFilters,
    SupplierState,
    getSelectedSuppliers,
    getSuppliers,
    getSupplierState,
    updateCheckboxes,
    searchSuppliers,
    getCheckboxState,
    getNextPageSuppliers,
    setCheckboxes,
    getCountries,
} from '@emi/shared/state/filters';
import { Paging } from '@emi/shared/constants';
import { CheckboxTreeNode, Page, Supplier } from '@emi/shared/models';
import { select, Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import * as Constants from '@emi/shared/constants';

@Component({
    selector: 'via-filter-suppliers',
    templateUrl: './filter-suppliers.component.html',
    styleUrls: ['./filter-suppliers.component.scss'],
})
export class FilterSuppliersComponent implements OnInit, OnDestroy {
    @Input()
    public applyClicked: EventEmitter<boolean>;

    @Input()
    public cancelClicked: EventEmitter<boolean>;

    @Input()
    public showApplyButton: boolean;

    @Input()
    public isOverlay: boolean;

    @Output()
    public showApplyButtonChange = new EventEmitter<boolean>();

    @ViewChild('scrollArea')
    public scrollArea: ElementRef;

    @ViewChildren('page')
    public pageElements: QueryList<ElementRef>;

    @ViewChildren('pageAnchor')
    public pageAnchorElements: QueryList<ElementRef>;

    private suppliersSubscription: Subscription;
    private applyClickedSubscription: Subscription;
    private cancelClickedSubscription: Subscription;
    private checkBoxSubscription: Subscription;
    private stateSubscription: Subscription;
    public suppliersTree: Array<CheckboxTreeNode> = [];
    public selectedSuppliers: Array<Supplier> = [];
    public stateSuppliers: Array<Supplier> = [];
    public loading: boolean;
    public showError = false;
    public errorMessage = '';
    public hasSearchedByText = false;
    public checkedArray: Array<CheckboxTreeNode> = [];
    public pages: Page[] = [];
    public totalSize: number | undefined;
    public eventsSubject: Subject<CheckboxTreeNode[]> = new Subject<CheckboxTreeNode[]>();
    private nextPageId: string | undefined;
    public noResultsMessage = '';
    private isLargeViewport: boolean;

    constructor(private readonly store: Store<SupplierState>, private changeDetector: ChangeDetectorRef) {}

    public onCheckboxStateChange(selectedCheckbox: CheckboxTreeNode): void {
        this.store.dispatch(updateCheckboxes({ checkbox: selectedCheckbox }));
    }

    public onScrollDown(): void {
        if (this.nextPageId) {
            this.store.dispatch(getNextPageSuppliers());
        }
    }

    public ngOnInit(): void {
        this.checkBoxSubscription = this.store.pipe(select(getCheckboxState)).subscribe((y) => {
            this.checkedArray = y.checkboxes;
        });

        this.suppliersSubscription = this.store.pipe(select(getSelectedSuppliers)).subscribe((suppliers) => {
            if (suppliers.length > 0) {
                if (this.checkedArray.length === 0) {
                    this.store.dispatch(
                        setCheckboxes({
                            checkboxes: suppliers.map((supplier) => {
                                return this.buildCheckboxTreeNode(supplier, true);
                            }),
                        })
                    );
                }
                this.selectedSuppliers = suppliers;
                this.suppliersTree = suppliers.map((supplier) => {
                    return this.buildCheckboxTreeNode(supplier);
                });
                const nextPage = new Page('', this.suppliersTree);
                this.pages.push(nextPage);
                this.eventsSubject.next(nextPage.value);
            }
            this.checkForResults();
        });

        this.stateSubscription = this.store.pipe(select(getSupplierState)).subscribe((state) => {
            this.loading = state.loading;
            this.showError = state.failed;
            this.errorMessage = state.error;
            this.nextPageId = state.metadata?.nextPageId;
            this.totalSize = state.metadata?.totalResults;
            this.stateSuppliers = state.suppliers;
            this.pages = [];
            if (this.selectedSuppliers.length === 0) {
                this.suppliersTree = state.suppliers.map((supplier) => {
                    return this.buildCheckboxTreeNode(supplier);
                });
            }
            this.eventsSubject.next(this.suppliersTree);

            while (this.suppliersTree.length > 0) {
                const newPage = this.suppliersTree.splice(0, Paging.PAGE_SIZE);
                this.pages.push(new Page(this.nextPageId, newPage));
            }

            this.checkForResults();

            if (!this.isLargeViewport || this.pages.length > Constants.Paging.LARGE_VIEWPORT_PAGE_THRESHOLD) {
                this.scrollToLastPage();
            }
        });

        this.applyClickedSubscription = this.applyClicked.subscribe(() => {
            const suppliers = this.checkedArray
                .map((supplier) => new Supplier(supplier.id, supplier.label))
                .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
            this.store.dispatch(addSuppliersSelection({ suppliers }));
            this.store.dispatch(getCountries());
            this.store.dispatch(clearFilters());
        });

        if (this.isOverlay) {
            this.cancelClickedSubscription = this.cancelClicked.subscribe(() => {
                this.pages = [];
                this.store.dispatch(
                    setCheckboxes({
                        checkboxes: this.selectedSuppliers.map((supplier) => {
                            return this.buildCheckboxTreeNode(supplier, true);
                        }),
                    })
                );
                const currentSelectableSuppliers = this.selectedSuppliers.length > 0 ? this.selectedSuppliers : this.stateSuppliers;
                this.suppliersTree = currentSelectableSuppliers.map((supplier) => {
                    return this.buildCheckboxTreeNode(supplier, this.selectedSuppliers.length > 0);
                });
                this.eventsSubject.next(this.suppliersTree);
                while (this.suppliersTree.length > 0) {
                    const newPage = this.suppliersTree.splice(0, Paging.PAGE_SIZE);
                    this.pages.push(new Page(this.nextPageId, newPage));
                }
            });
        }

        if (this.selectedSuppliers.length === 0 && this.stateSuppliers.length === 0) {
            this.store.dispatch(getSuppliers());
        }
    }

    public buildCheckboxTreeNode(supplier: Supplier, completed?: boolean): CheckboxTreeNode {
        if (completed) {
            return new CheckboxTreeNode(supplier.id, supplier.name, 'checked');
        }
        const existingSupplier = this.checkedArray.find((x) => x.id === supplier.id);
        return new CheckboxTreeNode(supplier.id, supplier.name, existingSupplier ? 'checked' : 'unchecked');
    }

    public searchFiltersByText(text: string): void {
        this.hasSearchedByText = true;
        this.store.dispatch(searchSuppliers({ searchText: text, delay: Constants.GlobalVariables.FILTER_SEARCH_DELAY }));
    }

    public resetSearch(): void {
        this.hasSearchedByText = false;
        this.store.dispatch(searchSuppliers({ searchText: '', delay: 0 }));
    }

    public selectionChanged(): void {
        this.showApplyButtonChange.emit(this.selectedSuppliers.length !== this.checkedArray.length && this.checkedArray.length > 0);
    }

    public checkForResults(): void {
        if (!this.loading && this.stateSuppliers.length == 0 && !this.hasSearchedByText && this.selectedSuppliers.length == 0) {
            this.noResultsMessage = Constants.GlobalVariables.ZERO_SKUS;
            return;
        }
        if (!this.loading && this.stateSuppliers.length == 0 && this.hasSearchedByText && this.selectedSuppliers.length == 0) {
            this.noResultsMessage = Constants.GlobalVariables.NO_SEARCH_RESULTS;
            return;
        }
        this.noResultsMessage = '';
    }

    public scrollToLastPage(): void {
        if (this.pageAnchorElements && this.pageAnchorElements.last && this.pageAnchorElements.last.nativeElement) {
            // TODO: investigate alternative to setTimeout
            setTimeout(() => {
                if (!this.isLargeViewport) {
                    this.pageAnchorElements.last.nativeElement.scrollIntoView();
                } else {
                    const pageAnchorIndex = this.pageAnchorElements.length - Constants.Paging.LARGE_VIEWPORT_PAGE_THRESHOLD;
                    this.pageAnchorElements.get(pageAnchorIndex)?.nativeElement.scrollIntoView();
                }
            }, 0);
        }
    }

    public ngOnDestroy(): void {
        if (this.hasSearchedByText && this.selectedSuppliers.length === 0) {
            this.store.dispatch(searchSuppliers({ searchText: '', delay: 0 }));
        }

        this.checkBoxSubscription.unsubscribe();
        this.stateSubscription.unsubscribe();
        this.suppliersSubscription.unsubscribe();
        this.applyClickedSubscription.unsubscribe();
        if (this.isOverlay) {
            this.cancelClickedSubscription.unsubscribe();
        }
        this.showApplyButtonChange.emit(false);
    }

    public checkScrollArea(): void {
        this.changeDetector.detectChanges();

        const pageHeight = this.pageElements.first.nativeElement.clientHeight;
        const scrollAreaHeight = this.scrollArea.nativeElement.clientHeight;
        const scrollDistance = this.scrollArea.nativeElement.scrollHeight;

        if (pageHeight <= scrollAreaHeight && this.totalSize && this.totalSize > Paging.PAGE_SIZE) {
            this.isLargeViewport = true;

            if (scrollDistance <= scrollAreaHeight) {
                // Load another page only if there is no scrollbar
                this.onScrollDown();
            }
        }
    }
}
